
import { Rental } from '@/services/types';
import { mockRentals } from '@/services/mockData';
import logger from '@/utils/logger';

interface RawRental {
  id: string;
  bike_id: string;
  courier_id: string;
  start_date: string;
  end_date: string | null;
  status: 'active' | 'completed' | 'cancelled' | 'pending';
  amount: number;
  total_mileage?: number;
  created_at?: string;
  updated_at?: string;
  human_number?: string;
  documents_url?: string;
  subscription_id?: string | null;
  // Вложенные данные из JOIN запросов
  couriers?: {
    id: string;
    name?: string;
    email?: string;
    phone?: string;
  } | null;
  bikes?: {
    id: string;
    model?: string;
    serial_number?: string;
  } | null;
  subscriptions?: Array<{
    id: string;
    status: 'active' | 'paused' | 'cancelled';
    payment_method_id: string | null;
  }>;
}

interface RawBike {
  id: string;
  model?: string;
  serial_number?: string;
}

interface RawCourier {
  id: string;
  name?: string;
  email?: string;
  phone?: string;
}

export const mapRentalsData = (
  rentalsData: RawRental[], 
  bikesData?: RawBike[], 
  couriersData?: RawCourier[]
): Rental[] => {
  if (!rentalsData || rentalsData.length === 0) {
    return mockRentals;
  }

  try {
    return rentalsData.map(rental => {
      // Добавляем детальное логирование для диагностики
      logger.debug('=== ДИАГНОСТИКА МАППИНГА АРЕНДЫ ===');
      logger.debug('ID аренды:', rental.id);
      logger.debug('Полные данные аренды:', rental);
      logger.debug('Данные курьера из JOIN (rental.couriers):', rental.couriers);
      logger.debug('Данные велосипеда из JOIN (rental.bikes):', rental.bikes);
      logger.debug('ID курьера:', rental.courier_id);
      logger.debug('ID велосипеда:', rental.bike_id);
      logger.debug('Переданные данные велосипедов:', bikesData);
      logger.debug('Переданные данные курьеров:', couriersData);
      
      // Проверяем различные варианты структуры данных
      let bikeFromJoin = rental.bikes;
      
      // Если bikes это массив, берем первый элемент
      if (Array.isArray(rental.bikes)) {
        bikeFromJoin = rental.bikes[0];
        logger.debug('Велосипед из массива:', bikeFromJoin);
      }
      
      // Поиск велосипеда в переданных данных
      const bikeFromArray = bikesData?.find(b => b.id === rental.bike_id);
      const bike = bikeFromJoin || bikeFromArray;
      
      logger.debug('Итоговые данные велосипеда:', bike);
      
      // Логика для имени велосипеда
      let bikeName: string;
      if (bike?.model && bike.model.trim()) {
        bikeName = bike.model;
        logger.debug('Используется модель велосипеда:', bikeName);
      } else if (bike?.serial_number && bike.serial_number.trim()) {
        bikeName = bike.serial_number;
        logger.debug('Используется серийный номер велосипеда:', bikeName);
      } else {
        bikeName = `Велосипед ${rental.bike_id.substring(0, 8)}`;
        logger.debug('Используется fallback для велосипеда:', bikeName);
      }
      
      // Аналогично для курьера
      let courierFromJoin = rental.couriers;
      
      // Если couriers это массив, берем первый элемент
      if (Array.isArray(rental.couriers)) {
        courierFromJoin = rental.couriers[0];
        logger.debug('Курьер из массива:', courierFromJoin);
      }
      
      // Поиск курьера в переданных данных
      const courierFromArray = couriersData?.find(c => c.id === rental.courier_id);
      const courier = courierFromJoin || courierFromArray;
      
      logger.debug('Итоговые данные курьера:', courier);
      
      // Логика для имени курьера
      let courierName: string;
      if (courier?.name && courier.name.trim()) {
        courierName = courier.name;
        logger.debug('Используется имя курьера:', courierName);
      } else {
        courierName = `Курьер ${rental.courier_id.substring(0, 8)}`;
        logger.debug('Используется fallback для курьера:', courierName);
      }
      
      // Проверяем, есть ли у аренды активная подписка
      const hasActiveSubscription = rental.subscriptions && 
        rental.subscriptions.some(sub => sub.status === 'active');
      
      // Формируем информацию о подписке для передачи в компоненты
      const subscriptionInfo = rental.subscription_id && rental.subscriptions && rental.subscriptions.length > 0
        ? rental.subscriptions[0]  // Используем первую подписку как основную
        : null;
      
      logger.debug(`Итоговый результат маппинга: курьер="${courierName}", велосипед="${bikeName}", статус="${rental.status}"`);
      logger.debug('=== КОНЕЦ ДИАГНОСТИКИ ===');
      
      return {
        id: rental.id,
        bike_id: rental.bike_id,
        courier_id: rental.courier_id,
        // Добавляем обязательные поля для типа Rental
        start_date: rental.start_date,
        end_date: rental.end_date,
        status: rental.status as 'active' | 'completed' | 'cancelled' | 'pending',
        total_mileage: rental.total_mileage || 0,
        // Добавляем совместимые псевдонимы
        bikeId: rental.bike_id,
        courierId: rental.courier_id,
        bikeName,
        courierName,
        startDate: rental.start_date,
        endDate: rental.end_date,
        cost: rental.amount || 0,
        human_number: rental.human_number,
        documentsUrl: rental.documents_url,
        subscription_id: rental.subscription_id,
        // Добавляем новые поля для статуса подписки
        subscriptionInfo,
        hasActiveSubscription,
        // Добавляем объекты курьера и велосипеда для совместимости
        courier: courier ? {
          id: courier.id,
          name: courier.name && courier.name.trim() ? courier.name : `Курьер ${courier.id.substring(0, 8)}`,
          email: courier.email || '',
          phone: courier.phone || ''
        } : undefined,
        bikes: bike ? {
          id: bike.id,
          model: bike.model && bike.model.trim() ? bike.model : 'Неизвестная модель',
          serial_number: bike.serial_number || 'Н/Д'
        } : undefined
      };
    });
  } catch (error) {
    logger.error('Ошибка при маппинге данных аренд:', error);
    return mockRentals;
  }
};
